import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getRandomSoalMatematika, getRandomSoalIPA,
  getRandomSoalBahasaIndonesia, getRandomSoalPengetahuanUmum,
  hitungSkorAkademik,
  soalMatematika, soalIPA, soalBahasaIndonesia, soalPengetahuanUmum
} from './bankSoalAkademik';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes } from './useResumeTes';

const SEMUA_BANK_AKADEMIK = [...soalMatematika, ...soalIPA, ...soalBahasaIndonesia, ...soalPengetahuanUmum];
function soalDariMetaAkademik(meta) {
  const byId = {};
  SEMUA_BANK_AKADEMIK.forEach(s => { byId[s.id] = s; });
  return (meta || []).map(m => (byId[m.id] ? { ...byId[m.id], jenis: m.jenis } : null)).filter(Boolean);
}

const DETIK_PER_SOAL = 56;
const JUMLAH_SOAL_MODE = {
  matematika: 20,
  ipa: 20,
  bahasa_indonesia: 20,
  pengetahuan_umum: 20,
  campuran: 80,
};
function waktuUntukMode(mode) {
  return (JUMLAH_SOAL_MODE[mode] || 20) * DETIK_PER_SOAL;
}

function formatWaktu(detik) {
  const m = Math.floor(detik / 60).toString().padStart(2, '0');
  const s = (detik % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function getBadge(skor) {
  if (skor >= 90) return { label: 'Luar Biasa', color: 'text-green-700 bg-green-100 border-green-300' };
  if (skor >= 75) return { label: 'Baik', color: 'text-blue-700 bg-blue-100 border-blue-300' };
  if (skor >= 60) return { label: 'Cukup', color: 'text-yellow-700 bg-yellow-100 border-yellow-300' };
  return { label: 'Perlu Latihan', color: 'text-red-700 bg-red-100 border-red-300' };
}

function getLabelJenis(jenis) {
  if (jenis === 'matematika')       return { label: 'Matematika',        icon: '📐', color: 'bg-blue-100 text-blue-700' };
  if (jenis === 'ipa')              return { label: 'IPA',               icon: '🔬', color: 'bg-green-100 text-green-700' };
  if (jenis === 'bahasa_indonesia') return { label: 'Bahasa Indonesia',  icon: '📝', color: 'bg-purple-100 text-purple-700' };
  return                                   { label: 'Pengetahuan Umum', icon: '🌏', color: 'bg-orange-100 text-orange-700' };
}

function BannerLanjutkanAkademik({ progresTersimpan, onLanjutkan, onMulaiBaru }) {
  if (!progresTersimpan) return null;
  const labelMode = progresTersimpan.mode === 'matematika' ? 'Matematika'
    : progresTersimpan.mode === 'ipa' ? 'IPA'
    : progresTersimpan.mode === 'bahasa_indonesia' ? 'Bahasa Indonesia'
    : progresTersimpan.mode === 'pengetahuan_umum' ? 'Pengetahuan Umum'
    : 'Campuran (TPA Lengkap)';
  const totalSoal = progresTersimpan.soalMeta?.length || 0;
  return (
    <div className="rounded-xl p-4 mb-5 border-2 border-indigo-400 bg-indigo-50">
      <p className="text-xs font-bold uppercase tracking-wide text-indigo-700 mb-1">⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm text-indigo-800 mb-3">
        Mode <b>{labelMode}</b>, baru sampai soal <b>{(progresTersimpan.indeks || 0) + 1}/{totalSoal}</b>, sisa waktu <b>{formatWaktu(progresTersimpan.waktuSisa || 0)}</b>.
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan} className="flex-1 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition">
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru} className="flex-1 py-2 rounded-lg border border-indigo-300 text-indigo-600 text-sm font-semibold hover:bg-indigo-100 transition">
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

// ─── Layar Intro ─────────────────────────────────────────────
function LayarIntro({ onMulai, onBack, tryoutMode, progresTersimpan, onLanjutkan, onMulaiBaru }) {
  const [mode, setMode] = useState(null);

  const MODES = [
    {
      id: 'matematika',
      icon: '📐',
      label: 'Matematika',
      desc: '20 soal — aritmatika, aljabar, deret angka',
      detail: 'Fokus latihan matematika saja, tidak tercampur tipe lain',
      border: 'border-blue-400', bg: 'bg-blue-50',
      iconBg: 'bg-blue-100', textColor: 'text-blue-700', checkColor: 'text-blue-600',
    },
    {
      id: 'ipa',
      icon: '🔬',
      label: 'IPA',
      desc: '20 soal — fisika, kimia, biologi dasar',
      detail: 'Fokus latihan IPA saja, tidak tercampur tipe lain',
      border: 'border-green-400', bg: 'bg-green-50',
      iconBg: 'bg-green-100', textColor: 'text-green-700', checkColor: 'text-green-600',
    },
    {
      id: 'bahasa_indonesia',
      icon: '📝',
      label: 'Bahasa Indonesia',
      desc: '20 soal — tata bahasa, ejaan, pemahaman wacana',
      detail: 'Fokus latihan Bahasa Indonesia saja, tidak tercampur tipe lain',
      border: 'border-purple-400', bg: 'bg-purple-50',
      iconBg: 'bg-purple-100', textColor: 'text-purple-700', checkColor: 'text-purple-600',
    },
    {
      id: 'pengetahuan_umum',
      icon: '🌏',
      label: 'Pengetahuan Umum',
      desc: '20 soal — wawasan kebangsaan dan kenegaraan',
      detail: 'Fokus latihan Pengetahuan Umum saja, tidak tercampur tipe lain',
      border: 'border-orange-400', bg: 'bg-orange-50',
      iconBg: 'bg-orange-100', textColor: 'text-orange-700', checkColor: 'text-orange-600',
    },
    {
      id: 'campuran',
      icon: '📚',
      label: 'Campuran (TPA Lengkap)',
      desc: '20 Matematika + 20 IPA + 20 B.Indonesia + 20 Pengetahuan Umum',
      detail: 'Simulasi TPA yang sesungguhnya',
      border: 'border-indigo-400', bg: 'bg-indigo-50',
      iconBg: 'bg-indigo-100', textColor: 'text-indigo-700', checkColor: 'text-indigo-600',
    },
  ];

  const selected = MODES.find(m => m.id === mode);
  const modeCampuran = MODES.find(m => m.id === 'campuran');

  // Dalam konteks Tryout resmi, mode dikunci ke Campuran — semua peserta
  // mengerjakan komposisi soal yang sama. Notice besar biar jelas kenapa
  // tidak ada pilihan mode.
  if (tryoutMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
          <div className="text-center mb-5">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">📚</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Kemampuan Akademik</h1>
            <p className="text-gray-400 text-sm mt-1">Bagian dari Tryout Resmi</p>
          </div>

          <BannerLanjutkanAkademik progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

          <div className="rounded-xl p-4 mb-4 border-2 border-amber-400 bg-amber-50">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-1">📌 Perhatian</p>
            <p className="text-sm font-semibold text-amber-800 leading-relaxed">
              Karena ini bagian dari Tryout resmi, mode dikunci otomatis ke <u>Campuran</u> — semua peserta mengerjakan komposisi soal yang sama, tidak bisa memilih mode lain.
            </p>
          </div>

          <div className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 ${modeCampuran.border} ${modeCampuran.bg} mb-5`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${modeCampuran.iconBg}`}>
              <span className="text-2xl">{modeCampuran.icon}</span>
            </div>
            <div className="flex-1">
              <p className={`font-bold text-sm ${modeCampuran.textColor}`}>{modeCampuran.label}</p>
              <p className="text-xs text-gray-500">{modeCampuran.desc}</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
            <div><p className="text-lg font-bold text-gray-800">{JUMLAH_SOAL_MODE.campuran}</p><p className="text-xs text-gray-500">Soal</p></div>
            <div><p className="text-lg font-bold text-gray-800">{Math.round(waktuUntukMode('campuran') / 60)}'</p><p className="text-xs text-gray-500">Waktu</p></div>
            <div><p className="text-lg font-bold text-gray-800">~{DETIK_PER_SOAL}"</p><p className="text-xs text-gray-500">Per soal</p></div>
          </div>

          <button onClick={() => onMulai('campuran')}
            className="w-full py-3 rounded-xl font-bold border-2 border-indigo-400 bg-indigo-50 text-indigo-700 hover:opacity-90 transition">
            Mulai Sekarang →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">📚</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Kemampuan Akademik</h1>
          <p className="text-gray-400 text-sm mt-1">Pilih mode latihan</p>
        </div>

        <BannerLanjutkanAkademik progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

        <div className="space-y-3 mb-5">
          {MODES.map(m => (
            <button key={m.id} onClick={() => setMode(m.id)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition text-left
                ${mode === m.id ? `${m.border} ${m.bg}` : 'border-gray-200 bg-white hover:border-gray-300'}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                ${mode === m.id ? m.iconBg : 'bg-gray-100'}`}>
                <span className="text-2xl">{m.icon}</span>
              </div>
              <div className="flex-1">
                <p className={`font-bold text-sm ${mode === m.id ? m.textColor : 'text-gray-700'}`}>{m.label}</p>
                <p className="text-xs text-gray-500">{m.desc}</p>
                {mode === m.id && <p className={`text-xs mt-1 font-medium ${m.textColor}`}>✦ {m.detail}</p>}
              </div>
              {mode === m.id && <span className={`text-xl font-bold ${m.checkColor}`}>✓</span>}
            </button>
          ))}
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
          <div><p className="text-lg font-bold text-gray-800">{JUMLAH_SOAL_MODE[mode] || 80}</p><p className="text-xs text-gray-500">Soal</p></div>
          <div><p className="text-lg font-bold text-gray-800">{Math.round(waktuUntukMode(mode || 'campuran') / 60)}'</p><p className="text-xs text-gray-500">Waktu</p></div>
          <div><p className="text-lg font-bold text-gray-800">~{DETIK_PER_SOAL}"</p><p className="text-xs text-gray-500">Per soal</p></div>
        </div>

        <div className="flex gap-3">
          <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">← Kembali</button>
          <button onClick={() => mode && onMulai(mode)} disabled={!mode}
            className={`flex-grow py-3 rounded-xl font-bold transition
              ${mode ? `${selected?.border} border-2 ${selected?.bg} ${selected?.textColor} hover:opacity-90` : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
            {mode ? `Mulai ${selected?.label} →` : 'Pilih mode dulu'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Soal ────────────────────────────────────────────────────
function SoalAkademik({ soal, onJawab, jawaban }) {
  const huruf = ['a', 'b', 'c', 'd', 'e'];
  const info = getLabelJenis(soal.jenis);

  const bgSoal = soal.jenis === 'matematika' ? 'bg-blue-50 border-blue-100'
    : soal.jenis === 'ipa' ? 'bg-green-50 border-green-100'
    : soal.jenis === 'bahasa_indonesia' ? 'bg-purple-50 border-purple-100'
    : 'bg-orange-50 border-orange-100';

  const borderPilihan = soal.jenis === 'matematika' ? 'border-blue-500 bg-blue-100 text-blue-800'
    : soal.jenis === 'ipa' ? 'border-green-500 bg-green-100 text-green-800'
    : soal.jenis === 'bahasa_indonesia' ? 'border-purple-500 bg-purple-100 text-purple-800'
    : 'border-orange-500 bg-orange-100 text-orange-800';

  const hoverPilihan = soal.jenis === 'matematika' ? 'hover:border-blue-300 hover:bg-blue-50'
    : soal.jenis === 'ipa' ? 'hover:border-green-300 hover:bg-green-50'
    : soal.jenis === 'bahasa_indonesia' ? 'hover:border-purple-300 hover:bg-purple-50'
    : 'hover:border-orange-300 hover:bg-orange-50';

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide ${info.color}`}>
          {info.icon} {info.label}
        </span>
      </div>
      <div className={`border rounded-xl p-4 mb-4 ${bgSoal}`}>
        <p className="text-base font-semibold text-gray-800 leading-relaxed">{soal.soal}</p>
      </div>
      <div className="space-y-2">
        {huruf.map(h => (
          (h in soal.pilihan) && (
            <button key={h} onClick={() => onJawab(h)}
              className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium transition text-sm
                ${jawaban === h ? borderPilihan : `border-gray-200 bg-white text-gray-700 ${hoverPilihan}`}`}>
              <span className="font-bold mr-2 uppercase">{h}.</span>{soal.pilihan[h]}
            </button>
          )
        ))}
      </div>
    </div>
  );
}

// ─── Kartu Skor ───────────────────────────────────────────────
function KartuSkor({ label, icon, hasil, borderColor, barColor }) {
  const badge = getBadge(hasil.skor);
  return (
    <div className={`rounded-xl p-4 border-2 ${borderColor}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">{icon} {label}</p>
          <p className="text-3xl font-extrabold text-gray-800 mt-1">{hasil.skor}</p>
          <p className="text-xs text-gray-400">dari 100 poin</p>
        </div>
        <span className={`text-xs font-bold px-3 py-1 rounded-full border ${badge.color}`}>{badge.label}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div className={`h-2 rounded-full ${barColor} transition-all duration-1000`} style={{ width: `${hasil.skor}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="bg-green-50 rounded-lg py-2"><p className="font-bold text-green-700 text-base">{hasil.benar}</p><p className="text-gray-500">Benar</p></div>
        <div className="bg-red-50 rounded-lg py-2"><p className="font-bold text-red-700 text-base">{hasil.salah}</p><p className="text-gray-500">Salah</p></div>
        <div className="bg-gray-50 rounded-lg py-2"><p className="font-bold text-gray-600 text-base">{hasil.kosong}</p><p className="text-gray-500">Kosong</p></div>
      </div>
    </div>
  );
}

// ─── Layar Hasil ─────────────────────────────────────────────
function LayarHasil({ mode, soalList, jawabanUser, onUlang, onBack, tryoutMode, tryoutId }) {
  const [showPembahasan, setShowPembahasan] = useState(false);

  const s = (jenis) => soalList.filter(s => s.jenis === jenis);
  const h = (jenis) => {
    const list = s(jenis);
    return list.length > 0 ? hitungSkorAkademik(list, jawabanUser) : null;
  };

  const hasilMat = h('matematika');
  const hasilIPA = h('ipa');
  const hasilBin = h('bahasa_indonesia');
  const hasilPum = h('pengetahuan_umum');
  const hasilTotal = hitungSkorAkademik(soalList, jawabanUser);
  const badge = getBadge(hasilTotal.skor);

  // Catat hasil tes ke progress tracker (sekali saat hasil ditampilkan)
  useEffect(() => {
    catatHasilTes('akademik', hasilTotal.skor, { mode, benar: hasilTotal.benar, salah: hasilTotal.salah, kosong: hasilTotal.kosong, total: hasilTotal.total }, tryoutMode, tryoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labelMode = mode === 'matematika' ? '📐 Matematika'
    : mode === 'ipa' ? '🔬 IPA'
    : mode === 'bahasa_indonesia' ? '📝 Bahasa Indonesia'
    : mode === 'pengetahuan_umum' ? '🌏 Pengetahuan Umum'
    : '📚 Campuran TPA';

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="text-4xl mb-2">{hasilTotal.skor >= 75 ? '🏆' : hasilTotal.skor >= 60 ? '👍' : '📚'}</div>
          <p className="text-xs text-gray-400 mb-1">Mode: {labelMode}</p>
          <h2 className="text-lg font-bold text-gray-800 mb-3">Hasil Tes Kemampuan Akademik</h2>
          <div className="text-6xl font-extrabold text-indigo-600 mb-1">{hasilTotal.skor}</div>
          <p className="text-gray-400 text-sm mb-3">Skor Total</p>
          <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold border ${badge.color}`}>{badge.label}</span>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-4 mb-2">
            <div className="bg-indigo-500 h-3 rounded-full transition-all duration-1000" style={{ width: `${hasilTotal.skor}%` }} />
          </div>
          <p className="text-xs text-gray-400">💡 Skor ini akan digabungkan ke nilai akhir psikotes kamu</p>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Rincian Skor per Kategori</p>
          {hasilMat && <KartuSkor label="Matematika" icon="📐" hasil={hasilMat} borderColor="border-blue-200" barColor="bg-blue-500" />}
          {hasilIPA && <KartuSkor label="IPA" icon="🔬" hasil={hasilIPA} borderColor="border-green-200" barColor="bg-green-500" />}
          {hasilBin && <KartuSkor label="Bahasa Indonesia" icon="📝" hasil={hasilBin} borderColor="border-purple-200" barColor="bg-purple-500" />}
          {hasilPum && <KartuSkor label="Pengetahuan Umum" icon="🌏" hasil={hasilPum} borderColor="border-orange-200" barColor="bg-orange-500" />}
        </div>

        <div className="bg-white rounded-2xl shadow p-4 space-y-3">
          <button onClick={() => setShowPembahasan(!showPembahasan)}
            className="w-full py-3 rounded-xl border border-indigo-300 text-indigo-600 font-semibold hover:bg-indigo-50 transition">
            {showPembahasan ? 'Sembunyikan' : '📋 Lihat'} Pembahasan
          </button>
          <div className="flex gap-3">
            <button onClick={onUlang} className="flex-1 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition">🔄 Ulangi</button>
            <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">← Menu</button>
          </div>
        </div>

        {showPembahasan && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Pembahasan Soal</p>
            {soalList.map((soal, idx) => {
              const jwb = jawabanUser.find(j => j.id === soal.id)?.jawaban;
              const benar = jwb === soal.jawaban;
              const info = getLabelJenis(soal.jenis);
              return (
                <div key={soal.id} className={`bg-white rounded-xl p-4 border-l-4 ${benar ? 'border-green-500' : 'border-red-400'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${info.color}`}>{idx + 1}. {info.icon} {info.label}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${benar ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {benar ? '✓ Benar' : '✗ Salah'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{soal.soal}</p>
                  <p className="text-sm">
                    <span className="text-gray-400">Jawabanmu: </span>
                    <span className={`font-bold ${benar ? 'text-green-600' : 'text-red-500'}`}>
                      {jwb ? `${jwb.toUpperCase()}. ${soal.pilihan[jwb]}` : '—'}
                    </span>
                    {!benar && (
                      <span className="ml-2 text-gray-400">
                        | Benar: <span className="font-bold text-green-600">{soal.jawaban.toUpperCase()}. {soal.pilihan[soal.jawaban]}</span>
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 bg-gray-50 rounded-lg px-3 py-2">💡 {soal.pembahasan}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Komponen Utama ───────────────────────────────────────────
export default function AkademikModul({ onBack, tryoutMode = false, tryoutId = null }) {
  const [fase, setFase]           = useState('intro');
  useCegahKeluarTes(fase === 'tes', onBack);
  const [mode, setMode]           = useState(null);
  const [soalList, setSoalList]   = useState([]);
  const [indeks, setIndeks]       = useState(0);
  const [jawabanUser, setJawabanUser] = useState([]);
  const [waktuSisa, setWaktuSisa] = useState(waktuUntukMode('campuran'));
  const timerRef                  = useRef(null);
  const [progresTersimpan, setProgresTersimpan] = useState(null);

  useEffect(() => {
    setProgresTersimpan(bacaProgresTes('akademik', tryoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAutosaveTes(fase === 'tes', 'akademik', tryoutId, {
    mode,
    soalMeta: soalList.map(s => ({ id: s.id, jenis: s.jenis })),
    indeks,
    jawabanUser,
    waktuSisa,
  });

  function generateSoal(selectedMode) {
    if (selectedMode === 'matematika') return getRandomSoalMatematika(20);
    if (selectedMode === 'ipa') return getRandomSoalIPA(20);
    if (selectedMode === 'bahasa_indonesia') return getRandomSoalBahasaIndonesia(20);
    if (selectedMode === 'pengetahuan_umum') return getRandomSoalPengetahuanUmum(20);
    // Mode campuran: kategori TIDAK diacak silang lagi (dulu di-shuffle penuh
    // sehingga soal 4 kategori bercampur acak dan user gampang bingung). Sekarang
    // tiap kategori dikerjakan berurutan per blok — tetap 1 modul/1 alur soal
    // 1..80, cuma bloknya jelas. Lihat juga divider "Mulai bagian ..." di layar soal.
    return [
      ...getRandomSoalMatematika(20), ...getRandomSoalIPA(20),
      ...getRandomSoalBahasaIndonesia(20), ...getRandomSoalPengetahuanUmum(20),
    ];
  }

  const handleMulai = useCallback((selectedMode) => {
    hapusProgresTes('akademik', tryoutId);
    setProgresTersimpan(null);
    const soal = generateSoal(selectedMode);
    setMode(selectedMode);
    setSoalList(soal);
    setIndeks(0);
    setJawabanUser(soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(waktuUntukMode(selectedMode));
    setFase('tes');
  }, [tryoutId]);

  const handleLanjutkan = useCallback(() => {
    if (!progresTersimpan) return;
    const soal = soalDariMetaAkademik(progresTersimpan.soalMeta);
    setMode(progresTersimpan.mode);
    setSoalList(soal);
    setIndeks(progresTersimpan.indeks || 0);
    setJawabanUser(progresTersimpan.jawabanUser || soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(progresTersimpan.waktuSisa || waktuUntukMode(progresTersimpan.mode));
    setFase('tes');
  }, [progresTersimpan]);

  const handleMulaiBaruDariBanner = useCallback(() => {
    hapusProgresTes('akademik', tryoutId);
    setProgresTersimpan(null);
  }, [tryoutId]);

  useEffect(() => {
    if (fase !== 'tes') return;
    timerRef.current = setInterval(() => {
      setWaktuSisa(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          hapusProgresTes('akademik', tryoutId);
          setFase('hasil');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [fase, tryoutId]);

  const handleJawab = useCallback((nilai) => {
    setJawabanUser(prev => prev.map(j => j.id === soalList[indeks].id ? { ...j, jawaban: nilai } : j));
  }, [indeks, soalList]);

  const handleNext = useCallback(() => {
    if (indeks < soalList.length - 1) setIndeks(i => i + 1);
    else { clearInterval(timerRef.current); hapusProgresTes('akademik', tryoutId); setFase('hasil'); }
  }, [indeks, soalList.length, tryoutId]);

  const handlePrev = useCallback(() => { if (indeks > 0) setIndeks(i => i - 1); }, [indeks]);
  const handleSelesai = useCallback(() => {
    clearInterval(timerRef.current);
    hapusProgresTes('akademik', tryoutId);
    setFase('hasil');
  }, [tryoutId]);
  const handleUlang = useCallback(() => {
    setFase('intro'); setMode(null); setSoalList([]); setIndeks(0);
    setJawabanUser([]); setWaktuSisa(waktuUntukMode('campuran'));
  }, []);

  if (fase === 'intro') return (
    <LayarIntro
      onMulai={handleMulai}
      onBack={onBack}
      tryoutMode={tryoutMode}
      progresTersimpan={progresTersimpan}
      onLanjutkan={handleLanjutkan}
      onMulaiBaru={handleMulaiBaruDariBanner}
    />
  );
  if (fase === 'hasil') return <LayarHasil mode={mode} tryoutMode={tryoutMode} tryoutId={tryoutId} soalList={soalList} jawabanUser={jawabanUser} onUlang={handleUlang} onBack={onBack} />;

  const soal = soalList[indeks];
  if (!soal) return null;

  const jawabanSoalIni = jawabanUser.find(j => j.id === soal.id)?.jawaban;
  const progress       = ((indeks + 1) / soalList.length) * 100;
  const warnaMeter     = waktuSisa < 600 ? 'bg-red-500' : waktuSisa < 1200 ? 'bg-yellow-400' : 'bg-indigo-500';
  const info           = getLabelJenis(soal.jenis);

  // Divider "Mulai bagian ..." — hanya di mode campuran, dan hanya di soal
  // pertama tiap blok kategori (kategori beda dari soal sebelumnya).
  const mulaiKategoriBaru = mode === 'campuran' && (indeks === 0 || soalList[indeks - 1]?.jenis !== soal.jenis);
  const jumlahKategoriIni = soalList.filter(s => s.jenis === soal.jenis).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className={`font-mono font-bold text-lg px-3 py-1 rounded-lg ${waktuSisa < 600 ? 'text-red-600 bg-red-50' : 'text-gray-700 bg-gray-100'}`}>
              ⏱ {formatWaktu(waktuSisa)}
            </div>
            <span className="text-sm text-gray-500 font-semibold">
              <span className="text-indigo-600">{indeks + 1}</span> / {soalList.length}
            </span>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${info.color}`}>{info.icon} {info.label}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all duration-300 ${warnaMeter}`} style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {mulaiKategoriBaru && (
          <div className={`flex items-center gap-2 rounded-xl px-4 py-2.5 mb-3 text-sm font-semibold border ${info.color}`}>
            <span>{info.icon}</span>
            <span>Mulai bagian: {info.label} ({jumlahKategoriIni} soal)</span>
          </div>
        )}
        <div className="bg-white rounded-2xl shadow p-6 mb-4">
          <SoalAkademik soal={soal} onJawab={handleJawab} jawaban={jawabanSoalIni} />
        </div>

        <div className="flex gap-3 mb-4">
          <button onClick={handlePrev} disabled={indeks === 0}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
            ← Sebelumnya
          </button>
          {indeks < soalList.length - 1 ? (
            <button onClick={handleNext} className="flex-grow py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition">Selanjutnya →</button>
          ) : (
            <button onClick={handleSelesai} className="flex-grow py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition">Selesai ✓</button>
          )}
        </div>

        <div className="flex flex-wrap gap-1 justify-center">
          {soalList.map((s, i) => {
            const dijawab = jawabanUser.find(j => j.id === s.id)?.jawaban;
            const sudah   = dijawab !== null && dijawab !== undefined && dijawab !== '';
            return (
              <button key={s.id} onClick={() => setIndeks(i)}
                className={`w-7 h-7 text-[10px] rounded-lg font-semibold transition
                  ${i === indeks ? 'bg-indigo-600 text-white'
                  : sudah ? 'bg-green-500 text-white'
                  : s.jenis === 'matematika' ? 'bg-blue-100 text-blue-700'
                  : s.jenis === 'ipa' ? 'bg-green-100 text-green-700'
                  : s.jenis === 'bahasa_indonesia' ? 'bg-purple-100 text-purple-700'
                  : 'bg-orange-100 text-orange-700'}`}>
                {i + 1}
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-400 mt-2">
          🟦 Aktif | 🟩 Dijawab{mode === 'campuran' ? ' | 🔵 Matematika | 🟢 IPA | 🟣 B.Indo | 🟠 Pengetahuan Umum' : ''}
        </p>
      </div>
    </div>
  );
}
